import { type MaxPageHeight, platformLayoutOptions$ } from "$root/components/PlatformRouter/layout-options";
import { writeAtom } from "@mdotm/shadcn/legacy/reactivity";
import { type WorkspaceContentChangeTrigger, trackWorkspaceStateChange } from "../analytics";
import { workspaceAdapter } from "../ctx/adapter";
import { workspaceRepository } from "../repository";
import type { SharedConceptService } from "../services/sharedConceptService";
import type { WorkspaceState } from "./state";

/**
 * Everything the store does that is not a state transition, behind one seam.
 *
 * One object rather than five constructor arguments, because the set is closed and small: an operation can
 * report to analytics, persist the document, ask the shared-concept API for something, and move the page.
 * Anything else an operation wants to do belongs in the reducer, and if it cannot be expressed there it is
 * a new field here and an argued one.
 */
export type WorkspaceSideEffects = {
	/** Reported *after* the state change, so the payload describes the workspace the user now sees. */
	track(state: WorkspaceState, trigger: WorkspaceContentChangeTrigger): void;
	/** The whole-document PUT. Idempotent by construction, which is what makes a failure survivable. */
	persist(state: WorkspaceState): Promise<void>;
	/** Shared concepts persist through their own API; this is the implementation of those effects. */
	sharedConcepts: SharedConceptService;
	scrollToTop(): void;
	setMaxPageHeight(maxPageHeight: MaxPageHeight): void;
};

/**
 * `setView`'s two consequences outside the workspace document.
 *
 * Separated from the rest because they are the two a test wants to keep *real*: both are observable
 * globals, so recording them instead of performing them would stop the assertion from being about anything.
 */
export const pageLayoutEffects = {
	scrollToTop() {
		window.scrollTo(0, 0);
	},
	setMaxPageHeight(maxPageHeight: MaxPageHeight) {
		writeAtom(platformLayoutOptions$, (current) => ({ ...current, maxPageHeight }));
	},
};

/**
 * The app's wiring.
 *
 * `persist` is deliberately *not* debounced, because nothing debounces it today either: the whole-document
 * PUT is enqueued per persisted action and the task queue runs them in order, which is what makes a
 * failure retryable and the next edit subsume it. Coalescing them would be a real improvement and a real
 * behaviour change — the "saving" indicator and the last edit before an unmount both depend on the timing
 * — so it belongs in its own commit. This is the one place it would go.
 */
export function createWorkspaceSideEffects(sharedConcepts: SharedConceptService): WorkspaceSideEffects {
	return {
		track: trackWorkspaceStateChange,
		async persist(state) {
			const uuid = state.meta.document?.uuid;
			if (!uuid) {
				throw new Error("Missing workspace uuid of null | undefined");
			}
			await workspaceRepository.updateWorkspace(uuid, workspaceAdapter.fromState(state));
		},
		sharedConcepts,
		...pageLayoutEffects,
	};
}
